import { useEffect, useRef } from 'react';
import useFocusTrap from './useFocusTrap';
import useDismissable from './useDismissable';

// Makes an always-mounted slide-out drawer behave as a modal dialog: `inert` while
// closed, focus trapped while open, focus restored on close, Escape to dismiss.
// Used by the CI rate/competitor detail drawers and the EI playbook slideout.
//
// Why `inert` matters: these drawers stay in the DOM when closed and are moved
// off-screen with `translate-x-full`. Off-screen is NOT hidden — without `inert`
// every control inside a closed drawer stays focusable, so tabbing the page walks
// into an invisible panel and focus vanishes. That was the defect this fixes.
//
// Attach `containerRef` to the sliding panel and `closeButtonRef` to its close
// button. Callers still supply role="dialog" / aria-modal / aria-label themselves —
// that's static markup, not behaviour.
export default function useDrawerDialog(open, onClose) {
  const containerRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  useFocusTrap(containerRef, open);
  useDismissable(open, onClose);

  // Set imperatively, not as an `inert={!open}` JSX prop: React 18 doesn't support
  // `inert` as a prop (React 19 does), so it would be dropped. Assigning the
  // attribute works on both. `aria-hidden` rides along for engines without `inert`.
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    if (open) {
      node.removeAttribute('inert');
      node.removeAttribute('aria-hidden');
    } else {
      node.setAttribute('inert', '');
      node.setAttribute('aria-hidden', 'true');
    }
  }, [open]);

  // Move focus in on open; put it back where it came from on close.
  //
  // Written as an explicit open/close branch rather than an effect cleanup: the
  // drawer never unmounts, so a cleanup would also fire on unrelated re-runs, and
  // on close the cleanup races the same commit that still has focus inside the
  // drawer — focus ended up staying on the close button. Branching on `open` and
  // restoring in the falsy branch happens after that commit, so it sticks.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open) {
      // Only capture the outside trigger on a genuine closed→open transition, so a
      // re-render while open can't overwrite it with a node inside the drawer.
      if (!wasOpenRef.current) previouslyFocusedRef.current = document.activeElement;
      wasOpenRef.current = true;
      closeButtonRef.current?.focus();
      return;
    }
    if (!wasOpenRef.current) return; // initial mount while closed — nothing to restore
    wasOpenRef.current = false;
    const prev = previouslyFocusedRef.current;
    previouslyFocusedRef.current = null;
    if (prev && typeof prev.focus === 'function' && document.contains(prev)) prev.focus();
  }, [open]);

  return { containerRef, closeButtonRef };
}
